import React, { useEffect, useState } from 'react';
import { Table, Spinner } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { formatDateTH } from '../utils/dateUtils';

const API = process.env.REACT_APP_API;

/**
 * Reusable per-session teaching-record detail for one teaching assignment
 * (teach_id). Fetches getTeachTrackingByTeachId and renders each lesson log
 * row with its attendance breakdown. Used by AttendanceByRoom and
 * TeachingRecordByTeacher when a subject row is expanded.
 */
export default function TeachTrackingDetail({ teachId }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teachId) return;
    let active = true;
    setLoading(true);
    axiosInstance
      .get(`${API}/getTeachTrackingByTeachId/${teachId}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (active) setRecords(res.data?.records || []); })
      .catch(() => { if (active) setRecords([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [teachId]);

  if (loading) {
    return (
      <div className="text-center py-3 text-muted">
        <Spinner animation="border" size="sm" className="me-2" />กำลังโหลดบันทึกการสอน...
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        <i className="ri-file-list-3-line me-1"></i>ยังไม่มีบันทึกการสอน
      </div>
    );
  }

  return (
    <Table size="sm" hover responsive className="mb-0 align-middle">
      <thead className="table-secondary">
        <tr>
          <th className="text-center" style={{ width: 40 }}>#</th>
          <th className="text-center" style={{ width: 110 }}>วันที่</th>
          <th className="text-center" style={{ width: 55 }}>คาบ</th>
          <th>หัวข้อที่สอน</th>
          <th className="text-center text-success" style={{ width: 55 }}>มา</th>
          <th className="text-center text-danger" style={{ width: 55 }}>ขาด</th>
          <th className="text-center text-warning" style={{ width: 55 }}>สาย</th>
          <th className="text-center text-info" style={{ width: 55 }}>ลา</th>
          <th className="text-center" style={{ width: 55 }}>รวม</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, idx) => {
          const total = Number(r.total_students) || 0;
          return (
            <tr key={r.tr_id ? `${r.tr_id}-${idx}` : idx}>
              <td className="text-center text-muted">{idx + 1}</td>
              <td className="text-center">{r.att_date ? formatDateTH(r.att_date) : '-'}</td>
              <td className="text-center">{r.period_order || '-'}</td>
              <td>{r.tr_topic || <span className="text-muted fst-italic">ไม่ระบุหัวข้อ</span>}</td>
              <td className="text-center">{total > 0 ? <span className="text-success fw-semibold">{Number(r.presents) || 0}</span> : '-'}</td>
              <td className="text-center">{total > 0 ? <span className="text-danger fw-semibold">{Number(r.absents) || 0}</span> : '-'}</td>
              <td className="text-center">{total > 0 ? <span className="text-warning fw-semibold">{Number(r.lates) || 0}</span> : '-'}</td>
              <td className="text-center">{total > 0 ? <span className="text-info fw-semibold">{Number(r.leaves) || 0}</span> : '-'}</td>
              <td className="text-center text-muted">{total > 0 ? total : '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
